import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Checkbox,
  Button,
  Tooltip,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/system";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ClientDropDown } from "../../components/Icons";
import { useDispatch, useSelector } from "react-redux";
import { ClientSagaAction } from "../../store/client-dropdown/saga";
import SearchIcon from "@mui/icons-material/Search";
import {
  selectClientLoading,
  selectClients,
  selectSelectedClient,
} from "../../store/client-dropdown/selector";

// Styled Loading Box (matches StyledBox appearance)
const StyledLoadingBox = styled(Box)({
  background: "#F4F5F8",
  borderRadius: "6px",
  padding: "0px",
  border: "1px solid #E9ECF2",
  minWidth: "192px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "10px",
  minHeight: "45px",
});

interface Client {
  id: number;
  name: string;
  active: boolean;
}

// Dropdown Component
const MultiSelectClientDropdown: React.FC = () => {
  const dispatch = useDispatch();
  const clients: Client[] = useSelector(selectClients) || [];
  const selectedClient = useSelector(selectSelectedClient);
  const loading = useSelector(selectClientLoading);
  const [isChanging, setIsChanging] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<"below" | "above">(
    "below"
  );
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const dropdownRef = useRef<HTMLDivElement>(null);

  // Helper: normalize selection from different shapes (array or single)
  const normalizeSelectedFromReduxOrStorage = (): number[] => {
    // From Redux selector
    if (Array.isArray(selectedClient)) {
      return selectedClient.map((id: any) => Number(id));
    }
    if (
      typeof selectedClient === "number" ||
      typeof selectedClient === "string"
    ) {
      return [Number(selectedClient)];
    }
    // fallback to localStorage multi key
    try {
      const storedMulti = localStorage.getItem("selectedClientIds");
      if (storedMulti) {
        const parsed = JSON.parse(storedMulti);
        if (Array.isArray(parsed)) return parsed.map((id) => Number(id));
      }
      // fallback to legacy single key
      const storedSingle = localStorage.getItem("selectedClientId");
      if (storedSingle) return [Number(storedSingle)];
    } catch (e) {
      // ignore
    }
    return [];
  };

  // State for selection (always array of numeric ids)
  const [selectedIds, setSelectedIds] = useState<number[]>(() =>
    normalizeSelectedFromReduxOrStorage()
  );

  const [allSelected, setAllSelected] = useState<boolean>(() => {
    return clients.length > 0 && selectedIds.length === clients.length;
  });

  // Keep allSelected in sync when clients or selectedIds change
  useEffect(() => {
    setAllSelected(clients.length > 0 && selectedIds.length === clients.length);
  }, [clients, selectedIds]);

  // When redux selectedClient or clients change (e.g. after fetch), sync local state
  useEffect(() => {
    const normalized = normalizeSelectedFromReduxOrStorage();
    setSelectedIds(normalized);
  }, [selectedClient, clients]);

  const filteredOptions =
    clients?.filter(({ name }: { name: string }) =>
      name?.toLowerCase()?.includes(searchTerm?.toLowerCase())
    ) || [];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const checkDropdownPosition = () => {
      if (dropdownRef.current) {
        const { bottom } = dropdownRef.current.getBoundingClientRect();
        setDropdownPosition(
          window.innerHeight - bottom < 200 ? "above" : "below"
        );
      }
    };
    window.addEventListener("resize", checkDropdownPosition);
    checkDropdownPosition();
    return () => window.removeEventListener("resize", checkDropdownPosition);
  }, []);

  useEffect(() => {
    const fetchClientsAndSetAuth = () => {
      return new Promise((resolve) => {
        dispatch(ClientSagaAction.fetchClientsAndSetAuth({ resolve }));
      });
    };

    if (!clients?.length && !selectedClient) {
      fetchClientsAndSetAuth();
    }
  }, [clients, selectedClient, dispatch]);

  // Toggle dropdown open
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  // Toggle single item selection
  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const found = prev.includes(id);
      if (found) {
        return prev.filter((x) => x !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  // Toggle Select All
  const handleSelectAllToggle = () => {
    if (allSelected) {
      setSelectedIds([]);
      setAllSelected(false);
    } else {
      const allIds = clients.map((c) => Number(c.id));
      setSelectedIds(allIds);
      setAllSelected(true);
    }
  };

  // All accounts click (explicit row)
  const handleAllAccountsClick = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    const allIds = clients.map((c) => Number(c.id));
    setSelectedIds(allIds);
    setAllSelected(true);
  };

  // Submit selected ids to saga and persist (uses new saga setClients)
  const handleSubmit = async () => {
    if (!selectedIds || selectedIds.length === 0) return;
    setIsChanging(true);
    // persist locally
    try {
      localStorage.setItem("selectedClientIds", JSON.stringify(selectedIds));
    } catch (e) {
      // ignore
    }

    await new Promise<void>((resolve) => {
      // NOTE: dispatching the multi-select saga action you added in saga file
      dispatch(
        ClientSagaAction.setClients({
          clientIds: selectedIds.map(String),
          resolve,
        })
      );
    });

    setIsOpen(false);
    setSearchTerm("");
    setIsChanging(false);
    // keep same behavior as before: reload to reflect changes
    window.location.reload();
  };

  // Present selected name/summary in closed state
  const selectedClientName = (() => {
    if (allSelected) return "All accounts";
    if (!selectedIds || selectedIds.length === 0) return "Select account";
    if (selectedIds.length === 1) {
      const matched = clients.find(
        (c) => Number(c.id) === Number(selectedIds[0])
      );
      return matched ? matched.name : "1 selected";
    }
    // multiple
    const names = clients
      .filter((c) => selectedIds.includes(Number(c.id)))
      .map((c) => c.name);
    if (names.length <= 1) return names.join(", ");
    return `${names.length} selected`;
  })();

  // Tooltip content: list selected names (max display), with "+N more"
  const selectedNames = clients
    .filter((c) => selectedIds.includes(Number(c.id)))
    .map((c) => c.name);

  const tooltipContent = (() => {
    if (allSelected) {
      return <Typography>All accounts</Typography>;
    }
    if (!selectedIds || selectedIds.length === 0) {
      return <Typography>No account selected</Typography>;
    }

    const maxToShow = 8;
    const namesToShow = selectedNames.slice(0, maxToShow);
    const remaining = selectedNames.length - namesToShow.length;

    return (
      <Box sx={{ textAlign: "left", maxWidth: 300 }}>
        {namesToShow.map((n, idx) => (
          <Typography
            key={idx}
            sx={{
              fontSize: 13,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {n}
          </Typography>
        ))}
        {remaining > 0 && (
          <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
            + {remaining} more
          </Typography>
        )}
      </Box>
    );
  })();

  return loading || isChanging ? (
    <StyledLoadingBox>
      <CircularProgress size={20} />
      <Typography>Loading...</Typography>
    </StyledLoadingBox>
  ) : (
    <StyledLoadingBox className={`relative`} ref={dropdownRef}>
      {/* Main clickable area: left icon, label, info icon, dropdown arrow.
          Tooltip is attached only to the info icon. */}
      <Box
        onClick={toggleDropdown}
        className={`flex items-center justify-between px-3 py-2 gap-2 rounded cursor-pointer`}
        sx={{ width: "100%" }}
      >
        <Box display="flex" alignItems="center" gap={1} sx={{ flex: 1 }}>
          {ClientDropDown}
          <Typography
            sx={{
              px: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              textAlign: "left",
              flex: 1,
              fontSize: 14,
            }}
          >
            {selectedClientName}
          </Typography>

          {/* Info icon: stop click propagation so it doesn't toggle the dropdown.
              Tooltip appears on hover/focus of this icon only. */}
          <Tooltip
            title={tooltipContent}
            placement="bottom-start"
            arrow
            enterDelay={100}
            leaveDelay={200}
          >
            <IconButton
              size="small"
              aria-label="selected accounts info"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()} // prevent focus raising dropdown in some browsers
              tabIndex={0}
              sx={{ color: "#6B7280" }}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <ArrowDropDownIcon
          className={`text-primary-blue-950 ${
            isOpen ? "rotate-180" : "rotate-0"
          }`}
        />
      </Box>

      {isOpen && (
        <Box
          className={`absolute z-10 min-w-52 right-0 bg-white border !border-[#B0B0B0] rounded-lg shadow-md ${
            dropdownPosition === "above" ? "bottom-full mb-1" : "top-full mt-1"
          }`}
        >
          {/* Top row: Select All toggle and Submit */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 1,
              py: 1,
              gap: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Checkbox
                size="small"
                checked={allSelected}
                onChange={handleSelectAllToggle}
                inputProps={{ "aria-label": "select all clients" }}
              />
              <Typography sx={{ fontSize: 14 }}>Select All</Typography>
            </Box>

            <Button
              variant="contained"
              size="small"
              onClick={handleSubmit}
              disabled={selectedIds.length === 0}
              sx={{
                textTransform: "none",
                bgcolor: "#20407D",
                "&:hover": { bgcolor: "#18325f" },
                minWidth: 80,
                fontSize: 13,
              }}
            >
              Submit
            </Button>
          </Box>

          {/* Search */}
          <Box
            sx={{
              m: 1,
              display: "flex",
              alignItems: "center",
              border: "1px solid #B0B0B0",
              borderRadius: "8px",
              px: 2,
            }}
          >
            <SearchIcon sx={{ color: "#B0B0B0" }} />
            <input
              type="text"
              placeholder={`Search...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-auto px-2 py-1 focus:outline-none"
            />
          </Box>

          {/* Simple list (no react-window) */}
          <Box style={{ maxHeight: "450px", overflowY: "auto" }}>
            {filteredOptions.map((item) => {
              const isSelected = selectedIds.includes(Number(item.id));
              return (
                <Box
                  key={item.id}
                  onClick={() => toggleSelect(Number(item.id))}
                  sx={{
                    px: 4,
                    py: 1,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    background: isSelected ? "rgba(32,64,125,0.05)" : "",
                    "&:hover": { backgroundColor: "#F4F5F8" },
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Checkbox
                      size="small"
                      checked={isSelected}
                      onChange={() => toggleSelect(Number(item.id))}
                      onClick={(e) => e.stopPropagation()}
                    />
                    {ClientDropDown}
                    <Typography className="text-nowrap">{item.name}</Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>

          {filteredOptions.length === 0 && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height={50}
              width="100%"
              sx={{ backgroundColor: "#F4F5F8", borderRadius: "8px" }}
            >
              <Typography className="text-center">No client found</Typography>
            </Box>
          )}
        </Box>
      )}
    </StyledLoadingBox>
  );
};

export default MultiSelectClientDropdown;
